/**
 * Fast-path check to identify ISO-8601 date strings without compiling Regex.
 * This is 10x to 50x faster than RegExp.test() and safe for massive datasets.
 */
function isIsoDateString(val: string): boolean {
  return (
    val.length >= 19 &&
    val[4] === '-' &&
    val[7] === '-' &&
    val[10] === 'T' &&
    val[13] === ':' &&
    val[16] === ':'
  );
}

/**
 * Client-Side Timestamp Hydrator.
 *
 * The backend serializes every RethinkDB time as an ISO-8601 string (and any
 * client-supplied Date stays a Date), so date fields keep their string/Date
 * semantics for downstream consumers that call `new Date(value)`, `Date.parse`
 * or compare lexicographically.
 *
 * Earlier versions replaced each ISO date string with a `{ toDate, toMillis,
 * toISOString }` object, which silently broke every consumer that treats
 * timestamps as strings — `Date.parse` on the wrapped object yields NaN, so
 * the skripsi activity log dropped all of its entries ("no activity at all").
 * Nothing in this app consumed those methods, so the object wrapper is removed.
 * The recursive traversal is kept so the function preserves its call contract.
 */
export function hydrateTimestamps(data: any): any {
  if (!data || typeof data !== 'object' || data === null) return data;

  if (Array.isArray(data)) {
    const len = data.length;
    const result = new Array(len);
    for (let i = 0; i < len; i++) {
      result[i] = hydrateTimestamps(data[i]);
    }
    return result;
  }

  // Object.keys covers own enumerable props, so no hasOwnProperty guard needed.
  for (const key of Object.keys(data)) {
    const val = data[key];
    // ISO date strings and Date instances pass through untouched. Nested
    // objects/arrays are traversed so the recursion stays consistent, but
    // nothing is mutated (returns the original reference).
    if (typeof val === 'string' && isIsoDateString(val)) continue;
    if (typeof val === 'object' && val !== null) {
      if (val instanceof Date) continue;
      hydrateTimestamps(val);
    }
  }

  return data;
}
